// ReminderService.js - Complete reminder/alarm system for your voice interface

class ReminderService {
  constructor() {
    this.reminders = new Map();
    this.nextId = 1;
    this.callbacks = {
      onReminderSet: null,
      onReminderTriggered: null,
      onReminderCancelled: null
    };
  }

  // Set callback functions for UI updates
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  // Parse natural language time inputs
  parseTimeInput(input) {
    const now = new Date();
    const lowerInput = input.toLowerCase();
    
    // Handle relative times (in X minutes/hours)
    const relativeMinutesMatch = lowerInput.match(/(?:in\s+)?(\d+)\s*(?:minute|min|m)s?/i);
    if (relativeMinutesMatch) {
      const minutes = parseInt(relativeMinutesMatch[1]);
      const reminderTime = new Date(now.getTime() + minutes * 60000);
      return {
        time: reminderTime,
        description: `Reminder in ${minutes} minute${minutes !== 1 ? 's' : ''}`,
        type: 'relative'
      };
    }

    const relativeHoursMatch = lowerInput.match(/(?:in\s+)?(\d+)\s*(?:hour|hr|h)s?/i);
    if (relativeHoursMatch) {
      const hours = parseInt(relativeHoursMatch[1]);
      const reminderTime = new Date(now.getTime() + hours * 3600000);
      return {
        time: reminderTime,
        description: `Reminder in ${hours} hour${hours !== 1 ? 's' : ''}`,
        type: 'relative'
      };
    }

    // Handle specific times (at 3:30 PM, at 2:30, etc.)
    const timeMatch = lowerInput.match(/(?:at\s+)?(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2] || '0');
      const period = timeMatch[3]?.toLowerCase();

      // Convert to 24-hour format
      if (period === 'pm' && hours !== 12) hours += 12;
      if (period === 'am' && hours === 12) hours = 0;
      
      // If no AM/PM specified and time is before current time, assume next day
      if (!period && (hours < now.getHours() || (hours === now.getHours() && minutes <= now.getMinutes()))) {
        hours += 12; // Try PM first
        if (hours >= 24) {
          // If that makes it tomorrow, use tomorrow's date
          const tomorrow = new Date(now);
          tomorrow.setDate(tomorrow.getDate() + 1);
          tomorrow.setHours(hours - 24, minutes, 0, 0);
          return {
            time: tomorrow,
            description: `Reminder at ${this.formatTime(tomorrow)}`,
            type: 'specific'
          };
        }
      }

      const reminderTime = new Date(now);
      reminderTime.setHours(hours, minutes, 0, 0);
      
      // If the time has passed today, set for tomorrow
      if (reminderTime <= now) {
        reminderTime.setDate(reminderTime.getDate() + 1);
      }

      return {
        time: reminderTime,
        description: `Reminder at ${this.formatTime(reminderTime)}`,
        type: 'specific'
      };
    }

    // Handle "tomorrow at X" format
    const tomorrowMatch = lowerInput.match(/tomorrow\s+(?:at\s+)?(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
    if (tomorrowMatch) {
      let hours = parseInt(tomorrowMatch[1]);
      const minutes = parseInt(tomorrowMatch[2] || '0');
      const period = tomorrowMatch[3]?.toLowerCase();

      if (period === 'pm' && hours !== 12) hours += 12;
      if (period === 'am' && hours === 12) hours = 0;

      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(hours, minutes, 0, 0);

      return {
        time: tomorrow,
        description: `Reminder tomorrow at ${this.formatTime(tomorrow)}`,
        type: 'specific'
      };
    }

    return null;
  }

  // Set a new reminder
  setReminder(timeInput, message = '') {
    const parsed = this.parseTimeInput(timeInput);
    if (!parsed) {
      throw new Error('Could not understand the time format. Try "in 5 minutes", "at 3:30 PM", or "tomorrow at 9 AM"');
    }

    const reminder = {
      id: this.nextId++,
      time: parsed.time,
      scheduledTime: parsed.time, // Add this alias for compatibility
      message: message || parsed.description,
      description: parsed.description,
      type: parsed.type,
      createdAt: new Date(),
      isActive: true
    };

    // Don't set timeout here anymore - we'll use checkTriggeredReminders instead
    this.reminders.set(reminder.id, reminder);

    // Notify UI
    if (this.callbacks.onReminderSet) {
      this.callbacks.onReminderSet(reminder);
    }

    return reminder;
  }

  // NEW: Check for triggered reminders (called by useEffect interval)
  checkTriggeredReminders() {
    const now = new Date();
    const triggered = [];

    this.reminders.forEach((reminder) => {
      if (reminder.isActive && reminder.time <= now) {
        triggered.push(reminder);
        this.triggerReminder(reminder.id);
      }
    });

    return triggered;
  }

  // Trigger a reminder
  triggerReminder(reminderId) {
    const reminder = this.reminders.get(reminderId);
    if (!reminder || !reminder.isActive) return;

    reminder.isActive = false;
    reminder.triggeredAt = new Date();

    // Notify UI
    if (this.callbacks.onReminderTriggered) {
      this.callbacks.onReminderTriggered(reminder);
    }

    // Keep in memory for a bit in case user wants to see recent reminders
    setTimeout(() => {
      this.reminders.delete(reminderId);
    }, 300000); // Remove after 5 minutes
  }

  // Cancel a reminder
  cancelReminder(reminderId) {
    const reminder = this.reminders.get(reminderId);
    if (!reminder) return false;

    // Clear timeout if it exists (for backwards compatibility)
    if (reminder.timeoutId) {
      clearTimeout(reminder.timeoutId);
    }

    reminder.isActive = false;
    
    if (this.callbacks.onReminderCancelled) {
      this.callbacks.onReminderCancelled(reminder);
    }

    this.reminders.delete(reminderId);
    return true;
  }

  // Get all active reminders
  getActiveReminders() {
    // Clean up any expired reminders first
    const now = new Date();
    const expired = [];
    
    this.reminders.forEach((reminder, id) => {
      if (reminder.isActive && reminder.time <= now) {
        expired.push(id);
      }
    });
    
    // Don't auto-trigger here, just return active ones
    return Array.from(this.reminders.values()).filter(r => r.isActive);
  }

  // Get reminder by ID
  getReminderById(id) {
    return this.reminders.get(id);
  }

  // Clear all reminders
  clearAllReminders() {
    // Clear any timeouts
    this.reminders.forEach(reminder => {
      if (reminder.timeoutId) {
        clearTimeout(reminder.timeoutId);
      }
    });
    
    this.reminders.clear();
  }

  // Format time for display
  formatTime(date) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  // Format relative time
  getTimeUntil(date) {
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    
    if (diff <= 0) return 'Now';
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  }

  // Check if a message contains a reminder request
  static detectReminderRequest(message) {
    const reminderKeywords = [
      /(?:set|create|make)\s+(?:a\s+)?(?:reminder|alarm)/i,
      /remind\s+me/i,
      /wake\s+me\s+up/i,
      /alert\s+me/i,
      /notification/i
    ];

    return reminderKeywords.some(pattern => pattern.test(message));
  }

  // Extract reminder details from a message
  static extractReminderFromMessage(message) {
    const lowerMessage = message.toLowerCase();
    
    // Extract time patterns
    const timePatterns = [
      /(?:in\s+)?(\d+)\s*(?:minute|min|m)s?/i,
      /(?:in\s+)?(\d+)\s*(?:hour|hr|h)s?/i,
      /(?:at\s+)?(\d{1,2}):?(\d{2})?\s*(am|pm)?/i,
      /tomorrow\s+(?:at\s+)?(\d{1,2}):?(\d{2})?\s*(am|pm)?/i
    ];

    let timeMatch = null;
    for (const pattern of timePatterns) {
      timeMatch = lowerMessage.match(pattern);
      if (timeMatch) break;
    }

    // Extract message content (everything after common reminder phrases)
    let reminderMessage = message;
    const prefixPatterns = [
      /(?:set|create|make)\s+(?:a\s+)?(?:reminder|alarm)\s+(?:to\s+|for\s+)?/i,
      /remind\s+me\s+(?:to\s+|about\s+)?/i,
      /wake\s+me\s+up\s+(?:to\s+|for\s+)?/i,
      /alert\s+me\s+(?:to\s+|about\s+)?/i
    ];

    for (const pattern of prefixPatterns) {
      reminderMessage = reminderMessage.replace(pattern, '').trim();
    }

    // Remove time expressions from the message
    if (timeMatch) {
      reminderMessage = reminderMessage.replace(timeMatch[0], '').trim();
    }

    // Clean up common time words
    reminderMessage = reminderMessage
      .replace(/\b(?:in|at|for|about|tomorrow)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    return {
      timeExpression: timeMatch ? timeMatch[0] : null,
      message: reminderMessage || 'Reminder'
    };
  }
}

export default ReminderService;